import { DatePicker, DayOfWeek, IDatePickerStrings, IDatePickerStyleProps, IDatePickerStyles, ITextFieldProps } from '@fluentui/react';
import { BwjInputBaseComponent, IBwjInputProps, IBwjInputState } from '../bwj-input-base';
import { BwjInput } from '../../entities/bwj-input';
import { BwjWarningLabel } from '../bwj-warning-label';
import { toDate, toUnixTimestamp } from '../../utils/date-time';
import { BwjValidityState } from '../../entities/bwj-validity-state';

export interface IBwjDateFieldProps extends IBwjInputProps<number | null | undefined> {
    placeholder?: string;
    minDate?: Date;
    maxDate?: Date;
    datePickerStrings?: IDatePickerStrings;
}

interface IState extends IBwjInputState<number | null | undefined> {}

const datePickerStrings: IDatePickerStrings = {
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  
    shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  
    shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  
    goToToday: 'Go to today',
    prevMonthAriaLabel: 'Go to previous month',
    nextMonthAriaLabel: 'Go to next month',
    prevYearAriaLabel: 'Go to previous year',
    nextYearAriaLabel: 'Go to next year',
    closeButtonAriaLabel: 'Close date picker'
  };

export class BwjDateField extends BwjInputBaseComponent<number | null | undefined, IBwjDateFieldProps, IState> {
    constructor(props: IBwjDateFieldProps) {
      super(props);

      this.handleChange = this.handleChange.bind(this);
      this.handleCalendarDismiss = this.handleCalendarDismiss.bind(this);
      this.onDateFieldBlur = this.onDateFieldBlur.bind(this);

      this.strings = datePickerStrings;

      let value: number | null | undefined = props.input.value;
      this.dateValue = !!value ? toDate(value) : undefined;

      this.state = {
          input: props.input
      };
    }

    protected handleComponentDidMount() {
        const value: number | null | undefined = this.props.input.value;
        this.dateValue = !!value ? toDate(value) : undefined;

        this.setState({
            input: this.props.input,
        });
    }

    protected handleComponentDidUpdate(prevProps:IBwjDateFieldProps) {
        this.strings = this.props.datePickerStrings ?? datePickerStrings;
        
        if(prevProps.input !== this.props.input) {
            const value = this.props.input.value;
            this.dateValue = !!value ? toDate(value) : undefined;

            this.setState({
                input: this.props.input,
            });
        }
    }

    private handleCalendarDismiss() {
        this.updateInputField();
    }

    private handleChange(date: Date | null | undefined) {
        this.dateValue = !!date ? date : undefined;
        const input = new BwjInput<number | undefined>(!!this.dateValue ?
            toUnixTimestamp(this.dateValue) : undefined);
        input.validity = this.state.input.validity;
        input.message = this.state.input.message;

        this.setState({
            input,
        });
    }

    private hasBlurred = false;
    private onDateFieldBlur(e: any) {
        //ignore the first blur (it's not real)
        if(this.hasBlurred === false) {
            this.hasBlurred = true;
            return;
        }

        this.handleBlur(e);
        if(!!this.props.onBlur) {
            this.props.onBlur();
        }
    }

    protected valuesAreEqual(prev: number | null | undefined, curr: number | null | undefined) {
        if(prev === curr) { return true; }
        if(!prev || !curr) { return false; }

        return toDate(prev).getTime() === toDate(curr).getTime();
    }
  
    public render() {
      const p = this.props;
      const s = this.state;
      const className = s.input.validity === BwjValidityState.Invalid || s.input.validity === BwjValidityState.ValidationError ? 'invalid' : 
      s.input.validity === BwjValidityState.Warning ? 'warning' : undefined;

      const onRenderWarning = (props: ITextFieldProps | undefined) => {
          return (<BwjWarningLabel text={props?.description} />);
      };

      const textFieldProps = () => {
        const props: ITextFieldProps = {
            onRenderDescription: onRenderWarning
        };
        
        if(className === 'invalid') { 
            props.errorMessage = s.input.message || '';
        } else if(className === 'warning') {
            props.description = s.input.message;
        }

        return props;
      };

      return (
          <div className={className}>
            <DatePicker id={p.id}
                className={className}
                isRequired={p.required}
                firstDayOfWeek={DayOfWeek.Monday}
                minDate={p.minDate}
                maxDate={p.maxDate}
                strings={this.strings}
                placeholder={p.placeholder}
                ariaLabel="Select a date"
                styles={this.getStyles}
                textField={textFieldProps()}
                label={p.label}
                onSelectDate={this.handleChange}
                onFocus={this.focusHandler}
                onBlur={this.onDateFieldBlur}
                onAfterMenuDismiss={this.handleCalendarDismiss}
                value={this.dateValue}
            />
          </div>
      );
    }

    private getStyles(props: IDatePickerStyleProps): Partial<IDatePickerStyles> {
        const { className, theme } = props;
        return className === 'invalid' ? {
            icon: {
                color: theme.semanticColors.errorText
            },
        } : {};
    }

    private dateValue: Date | undefined;
    private strings: IDatePickerStrings;
  }